import { useMemo } from "react";

import {
  ArrowTrendingUpIcon,
  ChartBarIcon,
  ClockIcon,
  CubeIcon,
  CurrencyPoundIcon,
  InboxStackIcon,
  ShoppingBagIcon,
  SparklesIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import HeadlineStat from "../shared/HeadlineStat";
import LoadingWheel from "../shared/LoadingWheel";
import PriceFormat from "../../utils/priceFormat";
import useCategories from "../../data/useCategories";
import { IPartCategory } from "../../models/Part";
import { IVehicle } from "../../models/Vehicle";

/**
 * Stylised stats strip rendered above the assemblies / parts table on the
 * Vehicle Job page and the Lead Parts page. Mirrors the visual grammar of
 * the Unlisted Parts dashboard — HeadlineStat tiles on top, rounded-xl
 * stats tables underneath with a slate uppercase header, divide-y rows
 * and a coloured value column.
 *
 * Two panels:
 *   1. Parts in stock for this vehicle by category — every part category
 *      from the master list, weighted against the vehicle id so totals
 *      stay stable per car but vary realistically across the fleet.
 *   2. Sales from similar vehicles — comparable donor-car sales over the
 *      last 90 days, grouped by category.
 *
 * Numbers are all deterministic-mock until the live analytics endpoint is
 * wired up, but they now factor in the donor vehicle's brand tier, trim,
 * age, fuel type and capacity so a Range Rover Sport SVR doesn't price
 * its parts like a Vauxhall Corsa.
 */

interface Props {
  vehicle: IVehicle | null | undefined;
  /** Extra seed material (e.g. job id / lead id) so results remain stable
   *  when the same vehicle is viewed from different contexts. */
  seedExtra?: string;
}

/** FNV-1a + mulberry32 — same deterministic PRNG used elsewhere in the app. */
function seededRand(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const between = (rand: () => number, min: number, max: number) =>
  Math.round(min + rand() * (max - min));

const betweenF = (rand: () => number, min: number, max: number) =>
  min + rand() * (max - min);

interface StockRow {
  name: string;
  inStock: number;
  available: number;
  listed: number;
  avgPrice: number;
  value: number;
  avgDaysInStock: number;
}

interface SimilarSalesRow {
  name: string;
  unitsSold: number;
  listedCount: number;
  sellThroughPct: number;
  medianDaysToSell: number;
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
  revenue: number;
  lastSoldDaysAgo: number;
}

interface MockVehicleStats {
  /* Stock side */
  nonZeroStockRows: StockRow[];
  totalInStock: number;
  totalAvailable: number;
  totalListed: number;
  totalStockValue: number;
  averagePartValue: number;
  averageDaysInStock: number;

  /* Sales side */
  salesRows: SimilarSalesRow[];
  similarVehicleCount: number;
  similarWindowDays: number;
  totalRevenue: number;
  totalUnits: number;
  averageSalePrice: number;
  overallSellThroughPct: number;
  medianDaysToSell: number;
  avgPartsPerDonor: number;
  avgRevenuePerDonor: number;

  /* Meta */
  totalCategories: number;
  stockedCategories: number;
  vehicleMultiplier: number;
}

/* ─────────────────────────────────────────────────────────────────────
 * Realistic UK used-parts base prices (median 2024/25 scrapyard market).
 * Values are for an average mid-market donor car and are then scaled by
 * the `vehicleMultiplier` helper (brand tier × trim × age × fuel).
 * ───────────────────────────────────────────────────────────────────── */
function realisticCategoryBasePrice(name: string): number {
  const n = (name ?? "").toLowerCase();

  // ── Drivetrain / big ticket ─────────────────────────────
  if (n.includes("engine") && !n.includes("mount") && !n.includes("ecu") && !n.includes("cover") && !n.includes("bay"))
    return 950;
  if (n.includes("gearbox") || n.includes("transmission")) return 675;
  if (n.includes("turbo") || n.includes("supercharger")) return 395;
  if (n.includes("differential") || n.includes("diff ")) return 285;
  if (n.includes("propshaft") || n.includes("prop shaft")) return 175;
  if (n.includes("driveshaft") || n.includes("drive shaft") || n.includes("cv joint")) return 115;
  if (n.includes("steering rack") || n.includes("steering box")) return 265;
  if (n.includes("power steering pump") || n.includes("ps pump")) return 75;
  if (n.includes("steering column")) return 145;

  // ── Emissions / exhaust ─────────────────────────────────
  if (n.includes("dpf") || n.includes("particulate") || n.includes("catalytic") || n.includes("cat conv")) return 255;
  if (n.includes("exhaust") && (n.includes("manifold") || n.includes("downpipe"))) return 135;
  if (n.includes("exhaust") || n.includes("silencer") || n.includes("backbox")) return 85;
  if (n.includes("egr")) return 75;

  // ── Fuel / cooling ──────────────────────────────────────
  if (n.includes("fuel tank")) return 85;
  if (n.includes("fuel pump")) return 95;
  if (n.includes("fuel rail") || n.includes("injector")) return 60;
  if (n.includes("radiator")) return 110;
  if (n.includes("intercooler")) return 95;
  if (n.includes("ac compressor") || n.includes("air con compressor") || n.includes("a/c compressor")) return 155;
  if (n.includes("ac condenser") || n.includes("air con condenser")) return 85;
  if (n.includes("heater matrix") || n.includes("heater core")) return 95;
  if (n.includes("water pump")) return 55;

  // ── Electrics ───────────────────────────────────────────
  if (n.includes("ecu") && (n.includes("engine") || n.includes("ecm"))) return 185;
  if (n.includes("ecu") || n.includes("control module") || n.includes("control unit")) return 140;
  if (n.includes("alternator")) return 115;
  if (n.includes("starter motor") || n.includes("starter")) return 80;
  if (n.includes("battery")) return 35;
  if (n.includes("fuse box") || n.includes("fuse board")) return 55;
  if (n.includes("wiring loom") || n.includes("harness")) return 120;
  if (n.includes("instrument cluster") || n.includes("speedo") || n.includes("dash cluster")) return 110;

  // ── Body exterior ──────────────────────────────────────
  if (n.includes("bonnet")) return 165;
  if (n.includes("tailgate") || n.includes("boot lid") || n.includes("bootlid")) return 195;
  if (n.includes("front bumper")) return 185;
  if (n.includes("rear bumper")) return 145;
  if (n.includes("bumper")) return 165;
  if (n.includes("wing") && !n.includes("mirror")) return 95;
  if (n.includes("sill") || n.includes("panel")) return 70;
  if (n.includes("grille") || n.includes("grill")) return 65;
  if (n.includes("spoiler")) return 110;
  if (n.includes("arch") || n.includes("arch liner")) return 35;

  // ── Lights ──────────────────────────────────────────────
  if ((n.includes("headlamp") || n.includes("headlight")) && (n.includes("led") || n.includes("xenon") || n.includes("hid") || n.includes("adaptive") || n.includes("matrix"))) return 285;
  if (n.includes("headlamp") || n.includes("headlight")) return 145;
  if (n.includes("fog light") || n.includes("foglamp")) return 45;
  if (n.includes("rear light") || n.includes("tail light") || n.includes("taillamp")) return 75;
  if (n.includes("indicator") || n.includes("side repeater")) return 18;
  if (n.includes("number plate light") || n.includes("plate lamp")) return 14;
  if (n.includes("interior light") || n.includes("roof light")) return 22;

  // ── Doors / glass ──────────────────────────────────────
  if (n.includes("door") && (n.includes("shell") || n.includes("complete"))) return 265;
  if (n.includes("door card") || n.includes("door panel") || n.includes("door trim")) return 55;
  if (n.includes("door handle")) return 28;
  if (n.includes("door mirror") || (n.includes("mirror") && (n.includes("electric") || n.includes("folding") || n.includes("heated")))) return 135;
  if (n.includes("mirror")) return 65;
  if (n.includes("windscreen")) return 135;
  if (n.includes("quarter glass") || n.includes("door glass") || (n.includes("window") && n.includes("glass"))) return 45;
  if (n.includes("window regulator") || n.includes("window motor")) return 65;

  // ── Wheels / suspension / brakes ───────────────────────
  if (n.includes("alloy") || (n.includes("wheel") && !n.includes("bearing") && !n.includes("arch") && !n.includes("steering"))) return 85;
  if (n.includes("wheel bearing") || n.includes("hub")) return 55;
  if (n.includes("shock absorber") || n.includes("damper") || n.includes("strut")) return 65;
  if (n.includes("spring") && !n.includes("spring clip")) return 30;
  if (n.includes("suspension arm") || n.includes("wishbone") || n.includes("control arm")) return 48;
  if (n.includes("anti roll bar") || n.includes("arb")) return 45;
  if (n.includes("brake caliper")) return 65;
  if (n.includes("brake disc") || n.includes("brake pad")) return 40;
  if (n.includes("brake master") || n.includes("servo")) return 75;
  if (n.includes("abs pump") || n.includes("abs module")) return 125;
  if (n.includes("handbrake")) return 55;

  // ── Interior ────────────────────────────────────────────
  if (n.includes("dashboard") || n.includes("dash assembly")) return 215;
  if (n.includes("seat") && (n.includes("leather") || n.includes("electric") || n.includes("heated"))) return 195;
  if (n.includes("seat") && !n.includes("belt") && !n.includes("frame") && !n.includes("runner")) return 105;
  if (n.includes("seat belt")) return 32;
  if (n.includes("airbag")) return 95;
  if (n.includes("steering wheel")) return 110;
  if (n.includes("gear knob") || n.includes("gear stick") || n.includes("shift")) return 35;
  if (n.includes("sunroof") || n.includes("sun roof") || n.includes("panoramic")) return 240;
  if (n.includes("headlining") || n.includes("roof lining")) return 85;
  if (n.includes("carpet")) return 55;
  if (n.includes("centre console") || n.includes("center console") || n.includes("arm rest") || n.includes("armrest")) return 70;
  if (n.includes("glove box") || n.includes("glovebox")) return 35;

  // ── Sensors / small electrics ──────────────────────────
  if (n.includes("camera")) return 135;
  if (n.includes("sensor") || n.includes("probe")) return 38;
  if (n.includes("switch") || n.includes("button")) return 24;
  if (n.includes("relay") || n.includes("solenoid")) return 22;
  if (n.includes("actuator") || n.includes("servo")) return 55;
  if (n.includes("motor")) return 70;
  if (n.includes("horn")) return 22;
  if (n.includes("wiper") || n.includes("arm") || n.includes("blade")) return 28;

  // ── Misc ────────────────────────────────────────────────
  if (n.includes("badge") || n.includes("emblem")) return 16;
  if (n.includes("trim") || n.includes("cover")) return 32;
  if (n.includes("bracket") || n.includes("mount")) return 22;
  if (n.includes("clip") || n.includes("fastener") || n.includes("grommet")) return 8;

  return 45;
}

type Tier = "common" | "mid" | "rare";
function stockTier(name: string): Tier {
  const n = name.toLowerCase();
  if (
    n.includes("door") ||
    n.includes("bumper") ||
    n.includes("wing") ||
    n.includes("mirror") ||
    n.includes("headlamp") ||
    n.includes("headlight") ||
    n.includes("rear light") ||
    n.includes("bonnet") ||
    n.includes("tailgate") ||
    n.includes("alloy") ||
    n.includes("wheel") ||
    n.includes("seat") ||
    n.includes("handle") ||
    n.includes("trim") ||
    n.includes("light")
  ) {
    return "common";
  }
  if (
    n.includes("engine") ||
    n.includes("gearbox") ||
    n.includes("transmission") ||
    n.includes("turbo") ||
    n.includes("ecu") ||
    n.includes("airbag") ||
    n.includes("compressor") ||
    n.includes("alternator") ||
    n.includes("starter") ||
    n.includes("pump") ||
    n.includes("radiator") ||
    n.includes("intercooler") ||
    n.includes("steering") ||
    n.includes("brake") ||
    n.includes("sensor") ||
    n.includes("module") ||
    n.includes("camera")
  ) {
    return "mid";
  }
  return "rare";
}

/* ─────────────────────────────────────────────────────────────────────
 * Vehicle-context multiplier. Realistic used-parts pricing depends much
 * more on the donor car than on the part itself. A BMW M5 headlamp is
 * 3-4× the price of a Corsa headlamp; a 2022 hybrid is worth more than
 * a 2006 petrol of the same make. We combine a few signals:
 *   - brand tier     (budget / mid / premium / ultra-premium)
 *   - trim / variant (M, AMG, RS, ST, VRS, SVR, etc.)
 *   - age            (steep decay beyond ~10y)
 *   - fuel type      (EV & hybrid parts relatively scarce → premium)
 *   - engine size    (larger engines & higher power → richer drivetrains)
 * ───────────────────────────────────────────────────────────────────── */
function computeVehicleMultiplier(
  vehicle: IVehicle | null | undefined
): number {
  const make = (vehicle?.combinedMake ?? vehicle?.dvlaMake ?? "").toLowerCase();
  const model = (
    vehicle?.combinedModel ??
    vehicle?.dvlaModel ??
    ""
  ).toLowerCase();
  const fuel = (
    vehicle?.combinedFuelType ??
    vehicle?.fuelType ??
    ""
  ).toLowerCase();
  const year = vehicle?.modelYear ?? vehicle?.dvlaYearOfManufacture ?? null;
  const cc = vehicle?.combinedEngineCapacity ?? vehicle?.engineCapacity ?? null;
  const bhp = vehicle?.maximumPowerBHP ?? null;

  // Brand tier.
  let brand = 1.0;
  if (
    /(ferrari|lamborghini|bentley|rolls.?royce|maserati|mclaren|aston martin|porsche)/.test(
      make
    )
  )
    brand = 2.35;
  else if (
    /(bmw|mercedes|audi|lexus|jaguar|land rover|range rover|tesla|volvo|mini|alfa romeo|infiniti|cadillac|genesis|polestar|lotus)/.test(
      make
    )
  )
    brand = 1.42;
  else if (
    /(ford|volkswagen|skoda|seat|peugeot|citroen|renault|toyota|nissan|honda|mazda|hyundai|kia|vauxhall|opel|subaru|mitsubishi)/.test(
      make
    )
  )
    brand = 1.0;
  else if (/(dacia|fiat|smart|suzuki|chery|byd|mg|proton|ssangyong|perodua)/.test(make))
    brand = 0.78;

  // Trim / performance variant.
  let trim = 1.0;
  const trimStr = `${model}`;
  if (
    /(\bm\s?performance\b|\bm3\b|\bm4\b|\bm5\b|\bm6\b|\bm8\b|\bamg\b|\brs\d\b|\brs\s?line\b|\bsvr\b|\bgt[rs]?\b|\bvrs\b|\bsti\b|\btype r\b|\bnismo\b)/.test(
      trimStr
    )
  )
    trim = 1.22;
  else if (/(sport|s.?line|st.?line|st\b|gti|r.?line)/.test(trimStr))
    trim = 1.09;

  // Age decay.
  let age = 1.0;
  if (year) {
    const a = Math.max(0, new Date().getFullYear() - year);
    if (a <= 3) age = 1.25;
    else if (a <= 6) age = 1.12;
    else if (a <= 10) age = 1.0;
    else if (a <= 15) age = 0.85;
    else if (a <= 20) age = 0.72;
    else age = 0.58;
  }

  // Fuel type.
  let fuelM = 1.0;
  if (fuel.includes("electric")) fuelM = 1.18;
  else if (fuel.includes("hybrid") || fuel.includes("plug")) fuelM = 1.1;
  else if (fuel.includes("diesel")) fuelM = 1.04;

  // Engine size / BHP nudge (affects drivetrain & turbo prices).
  let power = 1.0;
  if (typeof cc === "number" && cc > 0) {
    if (cc >= 5000) power = 1.25;
    else if (cc >= 3000) power = 1.14;
    else if (cc >= 2000) power = 1.05;
    else if (cc >= 1400) power = 1.0;
    else power = 0.95;
  } else if (typeof bhp === "number" && bhp > 0) {
    if (bhp >= 400) power = 1.25;
    else if (bhp >= 280) power = 1.14;
    else if (bhp >= 180) power = 1.05;
    else power = 0.97;
  }

  // Compose, clamped to a sensible range.
  const raw = brand * trim * age * fuelM * power;
  return Math.max(0.55, Math.min(2.8, raw));
}

function popularityMultiplier(
  vehicle: IVehicle | null | undefined
): number {
  const make = (vehicle?.combinedMake ?? vehicle?.dvlaMake ?? "").toLowerCase();
  const model = (
    vehicle?.combinedModel ??
    vehicle?.dvlaModel ??
    ""
  ).toLowerCase();
  if (/(ferrari|lamborghini|bentley|rolls|maserati|mclaren|aston)/.test(make))
    return 0.45;
  if (
    /(focus|fiesta|corsa|astra|golf|polo|octavia|qashqai|3 series|a class|a3|a4|c class|c-class|1 series)/.test(
      `${make} ${model}`
    )
  )
    return 1.35;
  if (/(bmw|mercedes|audi)/.test(make)) return 1.1;
  return 1.0;
}

/*
 * Stock / sales count distributions. These are intentionally modest —
 * a single-donor vehicle context should produce totals in the low
 * hundreds of parts and low-5-figures of stock value, not the sprawling
 * yard-wide numbers we'd expect from a top-of-funnel dashboard. A final
 * proportional clamp in `buildMockStats` caps the aggregate monetary
 * totals so even the rarest / most popular vehicles stay realistic.
 */
function seededStockCount(rand: () => number, tier: Tier, popMult: number): number {
  // Dampen popularity so a Corsa doesn't blow the budget.
  const scale = Math.min(1.25, 0.85 + (popMult - 1) * 0.35);
  if (tier === "common") {
    if (rand() > 0.32) return 0;
    const r2 = rand();
    if (r2 > 0.9) return Math.max(1, Math.round(between(rand, 4, 7) * scale));
    if (r2 > 0.6) return Math.max(1, Math.round(between(rand, 2, 4) * scale));
    return Math.max(1, Math.round(between(rand, 1, 2) * scale));
  }
  if (tier === "mid") {
    if (rand() > 0.22) return 0;
    const r2 = rand();
    if (r2 > 0.88) return Math.max(1, Math.round(between(rand, 3, 5) * scale));
    if (r2 > 0.55) return Math.max(1, Math.round(between(rand, 1, 3) * scale));
    return 1;
  }
  if (rand() > 0.12) return 0;
  return rand() > 0.7 ? 2 : 1;
}

function seededSalesCount(
  rand: () => number,
  tier: Tier,
  popMult: number
): number {
  const scale = Math.min(1.25, 0.85 + (popMult - 1) * 0.35);
  if (tier === "common") return Math.max(0, Math.round(between(rand, 0, 6) * scale));
  if (tier === "mid") return Math.max(0, Math.round(between(rand, 0, 3) * scale));
  return rand() > 0.78 ? 1 : 0;
}

function categoryAvgPrice(cat: IPartCategory, vehicleMult: number): number {
  const base =
    cat.averagePrice && cat.averagePrice > 0
      ? cat.averagePrice
      : realisticCategoryBasePrice(cat.name);
  return Math.max(5, Math.round(base * vehicleMult));
}

function buildMockStats(
  vehicle: IVehicle | null | undefined,
  categories: IPartCategory[],
  seedExtra?: string
): MockVehicleStats {
  const seed = `${vehicle?.id ?? ""}|${vehicle?.vrm ?? ""}|${
    vehicle?.combinedMake ?? ""
  }|${vehicle?.combinedModel ?? ""}|${seedExtra ?? ""}`;
  const rand = seededRand(seed || "default-seed");

  const vehicleMult = computeVehicleMultiplier(vehicle);
  const popMult = popularityMultiplier(vehicle);

  const sorted = [...categories].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );

  /* ───── Stock side ───── */
  let totalInStock = 0;
  let totalAvailable = 0;
  let totalListed = 0;
  let totalStockValue = 0;
  let totalDaysWeighted = 0;

  const allStockRows: StockRow[] = sorted.map((cat) => {
    const tier = stockTier(cat.name ?? "");
    const inStock = seededStockCount(rand, tier, popMult);
    const listed =
      inStock === 0
        ? 0
        : Math.min(inStock, Math.round(inStock * (0.55 + rand() * 0.4)));
    const held =
      inStock === 0 ? 0 : between(rand, 0, Math.min(3, Math.floor(inStock / 4)));
    const available = Math.max(0, inStock - held);
    const avgPrice = categoryAvgPrice(cat, vehicleMult);
    const value = inStock * avgPrice;

    // Realistic days-in-stock distribution: common parts ~30-80d,
    // mid 60-160d, rare 120-300d. Nudged up a little for slow movers
    // (lots of stock but low demand).
    const baseDays =
      tier === "common" ? between(rand, 24, 82) :
      tier === "mid" ? between(rand, 55, 165) :
      between(rand, 120, 310);
    // Dead stock penalty when we're sitting on > 4 of a common part.
    const avgDaysInStock =
      tier === "common" && inStock > 4 ? baseDays + between(rand, 10, 45) : baseDays;

    totalInStock += inStock;
    totalAvailable += available;
    totalListed += listed;
    totalStockValue += value;
    totalDaysWeighted += avgDaysInStock * inStock;

    return {
      name: cat.name,
      inStock,
      available,
      listed,
      avgPrice,
      value,
      avgDaysInStock,
    };
  });

  // Safety clamp — keep total stock value in a realistic £/k band for a
  // single donor context. When we drift above the cap (e.g. a very
  // premium car with a full category list) we scale every row's inStock
  // and value down proportionally and rebuild totals.
  const STOCK_VALUE_CAP = 18_000;
  if (totalStockValue > STOCK_VALUE_CAP) {
    const factor = STOCK_VALUE_CAP / totalStockValue;
    totalInStock = 0;
    totalAvailable = 0;
    totalListed = 0;
    totalStockValue = 0;
    totalDaysWeighted = 0;
    for (const row of allStockRows) {
      if (row.inStock === 0) continue;
      const scaled = Math.max(1, Math.round(row.inStock * factor));
      const scaleRow = scaled / row.inStock;
      row.inStock = scaled;
      row.available = Math.min(scaled, Math.max(0, Math.round(row.available * scaleRow)));
      row.listed = Math.min(scaled, Math.max(0, Math.round(row.listed * scaleRow)));
      row.value = scaled * row.avgPrice;
      totalInStock += row.inStock;
      totalAvailable += row.available;
      totalListed += row.listed;
      totalStockValue += row.value;
      totalDaysWeighted += row.avgDaysInStock * row.inStock;
    }
  }

  const nonZeroStockRows = allStockRows
    .filter((r) => r.inStock > 0)
    .sort((a, b) => b.value - a.value || b.inStock - a.inStock);

  const averagePartValue =
    totalInStock === 0 ? 0 : Math.round(totalStockValue / totalInStock);
  const averageDaysInStock =
    totalInStock === 0 ? 0 : Math.round(totalDaysWeighted / totalInStock);

  /* ───── Sales side ───── */
  const similarWindowDays = 90;
  const donorScale = Math.min(1.2, 0.85 + (popMult - 1) * 0.4);
  const similarVehicleCount = Math.max(
    2,
    Math.min(9, Math.round(between(rand, 2, 7) * donorScale))
  );

  let medianSamples: number[] = [];
  const rawSalesRows: SimilarSalesRow[] = sorted.map((cat) => {
    const tier = stockTier(cat.name ?? "");
    const units = seededSalesCount(rand, tier, popMult);
    const avg = categoryAvgPrice(cat, vehicleMult);

    // Price spread: condition-driven, wider for pricier items.
    const spread = tier === "common" ? 0.35 : tier === "mid" ? 0.55 : 0.7;
    const minPrice = Math.max(5, Math.round(avg * (1 - spread * (0.45 + rand() * 0.25))));
    const maxPrice = Math.round(avg * (1 + spread * (0.4 + rand() * 0.3)));
    const soldAvg = Math.max(
      minPrice,
      Math.min(maxPrice, Math.round(avg * (0.9 + rand() * 0.24)))
    );

    const listedCount =
      units === 0
        ? between(rand, 0, 2)
        : Math.max(units, Math.round(units / betweenF(rand, 0.48, 0.82)));
    const sellThroughPct =
      listedCount === 0 ? 0 : Math.round((units / listedCount) * 100);

    // Median days-to-sell: common items move fast, rare items sit.
    const medianDaysToSell =
      units === 0
        ? 0
        : tier === "common"
          ? between(rand, 6, 32)
          : tier === "mid"
            ? between(rand, 22, 78)
            : between(rand, 55, 160);
    if (units > 0) medianSamples.push(medianDaysToSell);

    const lastSoldDaysAgo =
      units === 0 ? similarWindowDays : Math.max(0, Math.round((similarWindowDays / Math.max(1, units)) * (0.25 + rand() * 0.9)));

    return {
      name: cat.name,
      unitsSold: units,
      listedCount,
      sellThroughPct,
      medianDaysToSell,
      avgPrice: soldAvg,
      minPrice,
      maxPrice,
      revenue: units * soldAvg,
      lastSoldDaysAgo,
    };
  });

  // Safety clamp — keep total sales revenue in the same realistic band
  // as the stock-value clamp so the two panels stay coherent.
  const REVENUE_CAP = 16_000;
  const rawRevenue = rawSalesRows.reduce((s, r) => s + r.revenue, 0);
  if (rawRevenue > REVENUE_CAP) {
    const factor = REVENUE_CAP / rawRevenue;
    for (const row of rawSalesRows) {
      if (row.unitsSold === 0) continue;
      const scaled = Math.max(1, Math.round(row.unitsSold * factor));
      const scaleRow = scaled / row.unitsSold;
      row.unitsSold = scaled;
      row.listedCount = Math.max(scaled, Math.round(row.listedCount * scaleRow));
      row.sellThroughPct =
        row.listedCount === 0
          ? 0
          : Math.round((row.unitsSold / row.listedCount) * 100);
      row.revenue = scaled * row.avgPrice;
    }
  }

  const salesRows = rawSalesRows
    .filter((r) => r.unitsSold > 0)
    .sort((a, b) => b.revenue - a.revenue || b.unitsSold - a.unitsSold)
    .slice(0, 16);

  const totalUnits = rawSalesRows.reduce((s, r) => s + r.unitsSold, 0);
  const totalListedForSale = rawSalesRows.reduce(
    (s, r) => s + r.listedCount,
    0
  );
  const totalRevenue = rawSalesRows.reduce((s, r) => s + r.revenue, 0);
  const averageSalePrice =
    totalUnits === 0 ? 0 : Math.round(totalRevenue / totalUnits);
  const overallSellThroughPct =
    totalListedForSale === 0
      ? 0
      : Math.round((totalUnits / totalListedForSale) * 100);

  const sortedMedians = medianSamples.sort((a, b) => a - b);
  const medianDaysToSell =
    sortedMedians.length === 0
      ? 0
      : sortedMedians[Math.floor(sortedMedians.length / 2)];

  const avgPartsPerDonor =
    similarVehicleCount === 0
      ? 0
      : Math.round((totalUnits / similarVehicleCount) * 10) / 10;
  const avgRevenuePerDonor =
    similarVehicleCount === 0
      ? 0
      : Math.round(totalRevenue / similarVehicleCount);

  return {
    nonZeroStockRows,
    totalInStock,
    totalAvailable,
    totalListed,
    totalStockValue,
    averagePartValue,
    averageDaysInStock,

    salesRows,
    similarVehicleCount,
    similarWindowDays,
    totalRevenue,
    totalUnits,
    averageSalePrice,
    overallSellThroughPct,
    medianDaysToSell,
    avgPartsPerDonor,
    avgRevenuePerDonor,

    totalCategories: sorted.length,
    stockedCategories: nonZeroStockRows.length,
    vehicleMultiplier: vehicleMult,
  };
}

function DemoPill() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
      title="Placeholder data until the live analytics endpoint is wired up"
    >
      <SparklesIcon className="h-3 w-3" aria-hidden="true" />
      Demo data
    </span>
  );
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {subtitle && (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        )}
      </div>
      <DemoPill />
    </div>
  );
}

function StockCountBadge({ count }: { count: number }) {
  const tone =
    count < 3
      ? "bg-amber-50 text-amber-700 ring-amber-200"
      : count < 8
        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
        : "bg-sky-50 text-sky-700 ring-sky-200";
  return (
    <span
      className={classNames(
        "inline-flex min-w-[2.25rem] justify-center rounded-md px-1.5 py-0.5 text-xs font-semibold ring-1 ring-inset",
        tone
      )}
    >
      {count.toLocaleString("en-GB")}
    </span>
  );
}

function SellThroughPill({ pct }: { pct: number }) {
  const tone =
    pct >= 70
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
      : pct >= 45
        ? "bg-sky-50 text-sky-700 ring-sky-200"
        : pct >= 25
          ? "bg-amber-50 text-amber-700 ring-amber-200"
          : "bg-rose-50 text-rose-700 ring-rose-200";
  return (
    <span
      className={classNames(
        "inline-flex min-w-[3rem] justify-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset tabular-nums",
        tone
      )}
    >
      {pct}%
    </span>
  );
}

function formatLastSold(daysAgo: number): string {
  if (daysAgo <= 0) return "today";
  if (daysAgo === 1) return "1d ago";
  if (daysAgo < 14) return `${daysAgo}d ago`;
  if (daysAgo < 56) return `${Math.round(daysAgo / 7)}w ago`;
  return `${Math.round(daysAgo / 30)}mo ago`;
}

function formatPriceRange(lo: number, hi: number): string {
  if (lo === hi) return PriceFormat(lo);
  return `${PriceFormat(lo)}–${PriceFormat(hi)}`;
}

export default function JobAssemblyStats({ vehicle, seedExtra }: Props) {
  const { categories } = useCategories();
  const categoryList = categories.data?.data ?? [];

  const stats = useMemo(
    () => buildMockStats(vehicle, categoryList, seedExtra),
    [vehicle, categoryList, seedExtra]
  );

  const vehicleLabel = useMemo(() => {
    const parts = [vehicle?.combinedMake, vehicle?.combinedModel].filter(
      Boolean
    );
    return parts.length > 0 ? parts.join(" ") : "this vehicle";
  }, [vehicle?.combinedMake, vehicle?.combinedModel]);

  const loading = categories.isLoading && categoryList.length === 0;

  if (loading) {
    return (
      <div className="flex justify-center rounded-xl border border-slate-200 bg-white py-10 shadow-sm">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {/* ────────── Parts in stock — by category ────────── */}
      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <SectionHeader
          title="Parts in stock — by category"
          subtitle={`${stats.stockedCategories.toLocaleString(
            "en-GB"
          )} of ${stats.totalCategories.toLocaleString(
            "en-GB"
          )} categories currently stocked from comparable ${vehicleLabel} donors.`}
        />

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <HeadlineStat
            compact
            value={stats.totalInStock.toLocaleString("en-GB")}
            label="In stock"
            tone="cyan"
            icon={InboxStackIcon}
            sublabel={`${stats.totalListed.toLocaleString("en-GB")} listed`}
          />
          <HeadlineStat
            compact
            value={stats.totalAvailable.toLocaleString("en-GB")}
            label="Available"
            tone="emerald"
            icon={CubeIcon}
          />
          <HeadlineStat
            compact
            value={PriceFormat(stats.totalStockValue)}
            label="Stock value"
            tone="blue"
            icon={CurrencyPoundIcon}
            sublabel={`avg ${PriceFormat(stats.averagePartValue)}`}
          />
          <HeadlineStat
            compact
            value={`${stats.averageDaysInStock}d`}
            label="Avg age"
            tone="violet"
            icon={ClockIcon}
            sublabel="weighted by stock"
          />
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Stocked categories
              </span>
              <p className="text-xs text-slate-500">
                Sorted by total stock value
              </p>
            </div>
          </div>
          <div className="max-h-80 overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50/95 text-[10px] uppercase tracking-wide text-slate-500 backdrop-blur">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">
                    Category
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">Stock</th>
                  <th className="px-3 py-2 text-right font-semibold">Avail.</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Avg days
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Avg price
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.nonZeroStockRows.length > 0 ? (
                  stats.nonZeroStockRows.map((row, i) => (
                    <tr
                      key={`${row.name}-${i}`}
                      className="hover:bg-slate-50/80"
                    >
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {row.name}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <StockCountBadge count={row.inStock} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {row.available.toLocaleString("en-GB")}
                      </td>
                      <td
                        className={classNames(
                          "px-3 py-2 text-right tabular-nums",
                          row.avgDaysInStock > 180
                            ? "text-rose-600"
                            : row.avgDaysInStock > 90
                              ? "text-amber-700"
                              : "text-slate-700"
                        )}
                      >
                        {row.avgDaysInStock}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {PriceFormat(row.avgPrice)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-emerald-700">
                        {PriceFormat(row.value)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-xs text-slate-400"
                    >
                      No stock found for this vehicle's comparable donors.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ────────── Sales from similar vehicles ────────── */}
      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <SectionHeader
          title="Sales from similar vehicles"
          subtitle={`Last ${stats.similarWindowDays} days across ${stats.similarVehicleCount} similar ${vehicleLabel} donor ${
            stats.similarVehicleCount === 1 ? "car" : "cars"
          } · avg ${stats.avgPartsPerDonor.toLocaleString("en-GB", {
            maximumFractionDigits: 1,
          })} parts / ${PriceFormat(stats.avgRevenuePerDonor)} per donor.`}
        />

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <HeadlineStat
            compact
            value={PriceFormat(stats.totalRevenue)}
            label="Revenue"
            tone="emerald"
            icon={ArrowTrendingUpIcon}
            sublabel={`avg ${PriceFormat(stats.averageSalePrice)} / sale`}
          />
          <HeadlineStat
            compact
            value={stats.totalUnits.toLocaleString("en-GB")}
            label="Units sold"
            tone="cyan"
            icon={ShoppingBagIcon}
            sublabel={`${stats.overallSellThroughPct}% sell-through`}
          />
          <HeadlineStat
            compact
            value={`${stats.medianDaysToSell}d`}
            label="Median DTS"
            tone="violet"
            icon={ClockIcon}
            sublabel="typical time on site"
          />
          <HeadlineStat
            compact
            value={stats.similarVehicleCount.toLocaleString("en-GB")}
            label="Donor cars"
            tone="blue"
            icon={TruckIcon}
            sublabel={`Last ${stats.similarWindowDays}d`}
          />
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Top selling categories
              </span>
              <p className="text-xs text-slate-500">
                Showing top {stats.salesRows.length} by revenue
              </p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              <ChartBarIcon className="h-3 w-3" aria-hidden="true" />
              {stats.similarWindowDays}-day window
            </span>
          </div>
          <div className="max-h-80 overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50/95 text-[10px] uppercase tracking-wide text-slate-500 backdrop-blur">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">
                    Category
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">Units</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Sold&nbsp;%
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">DTS</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Price range
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Last sold
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.salesRows.length > 0 ? (
                  stats.salesRows.map((row, i) => (
                    <tr
                      key={`${row.name}-${i}`}
                      className="hover:bg-slate-50/80"
                    >
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {row.name}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                        {row.unitsSold.toLocaleString("en-GB")}
                        <span className="ml-1 text-[10px] text-slate-400">
                          /{row.listedCount.toLocaleString("en-GB")}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <SellThroughPill pct={row.sellThroughPct} />
                      </td>
                      <td
                        className={classNames(
                          "px-3 py-2 text-right tabular-nums",
                          row.medianDaysToSell > 90
                            ? "text-rose-600"
                            : row.medianDaysToSell > 45
                              ? "text-amber-700"
                              : "text-slate-700"
                        )}
                      >
                        {row.medianDaysToSell}d
                      </td>
                      <td
                        className="px-3 py-2 text-right tabular-nums text-slate-700"
                        title={`Avg sold ${PriceFormat(row.avgPrice)}`}
                      >
                        {formatPriceRange(row.minPrice, row.maxPrice)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                        {formatLastSold(row.lastSoldDaysAgo)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-emerald-700">
                        {PriceFormat(row.revenue)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-6 text-center text-xs text-slate-400"
                    >
                      No comparable sales in the last {stats.similarWindowDays}{" "}
                      days.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
